import React from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  Upload,
  notification,
  Row,
  Select,
} from "antd";
import importHospitalStaffStyles from "./ImportHospitalStaff.module.css";
import { useEffect, useState } from "react";
import { useUserStore } from "../../../../context/UserStore";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { UploadOutlined } from "@ant-design/icons";
import Papa from "papaparse";

export default function ImportHospitalStaff() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const defaultHospitalId = id || null;
  const defaultImportType = location.pathname.includes("/admin/users/import")
    ? "managers"
    : location.pathname.includes("/admin/employees/import")
      ? "employees"
      : null;
  const [hospitals, setHospitals] = useState([]);
  const [isLoadingHospitals, setIsLoadingHospitals] = useState(true);
  const [hospitalId, setHospitalId] = useState(defaultHospitalId);
  const [importType, setImportType] = useState(defaultImportType);
  const user = useUserStore((state) => state.user);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [csvData, setCsvData] = useState({});
  const [form] = Form.useForm();

  const loadHospitals = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/admin/hospitals/list`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${user.token}`,
        },
        type: "json",
      },
    )
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          throw new Error(response.statusText);
        }
      })
      .then((response) => {
        const newData = response.hospitals.data.map((item) => {
          return {
            label: item.attributes.name,
            value: String(item.attributes.id),
            key: item.id,
          };
        });
        setHospitals(newData);
        setIsLoadingHospitals(false);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const uploadStaff = async () => {
    setUploading(true);
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/admin/hospitals/${hospitalId}/upload_staff`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({
          hospital_staff: {
            hospitalId: hospitalId,
            importType: importType,
            employees: csvData,
          },
        }),
        type: "json",
      },
    )
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw body;
        }
        return body;
      })
      .then((response) => {
        setFileList([]);
        notification.success({
          message: response.message,
          description: response.message,
        });
        setUploading(false);
        navigate(`/admin/users`);
      })
      .catch((error) => {
        const errors = Array.isArray(error.errors)
          ? error.errors.join("\n")
          : error.errors || error.message || "The import could not be completed.";
        notification.error({
          message: error.message || "Import failed.",
          description: errors,
        });
        setUploading(false);
      });
  };

  //const handleFileChange = ({ fileList: newFileList }) => setFileList(newFileList);

  const props = {
    onRemove: (file) => {
      setFileList([]);
      setCsvData({});
    },
    beforeUpload: (file) => {
      setFileList([file]);

      Papa.parse(file, {
        header: true,
        skipEmptyLines: "greedy",
        complete: function (results) {
          setCsvData(results.data);
        },
      });

      return false;
    },
    fileList,
  };

  const handleUpload = () => {
    uploadStaff(csvData);
  };

  useEffect(() => {
    loadHospitals();
  }, []);

  return (
    <>
      <div
        className={`py-5 grid grid-cols-1 mb-5 ${importHospitalStaffStyles.headerBar}`}
      >
        <h1 className="text-4xl text-center text-white">Import Staff</h1>
      </div>
      <Row>
        <Col span={20}>
          <Form
            form={form}
            initialValues={{
              hospital_id: defaultHospitalId,
              import_type: defaultImportType,
            }}
            layout="vertical"
          >
            <Alert
              className="mb-5"
              message="Staff Import Instructions"
              description={
                <div className="text-sm leading-6">
                  <div className="mb-3">
                    <strong>Import order:</strong> managers first, then employees.
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                    <div>
                      <strong>CSV columns</strong>
                      <div className="mt-1 font-mono text-xs leading-5">
                        email, first_name, last_name, job_title, dept_name
                      </div>
                      <div className="mt-1 text-xs leading-5">
                        Optional columns: employee_id, dept_id,
                        manager_employee_id
                      </div>
                    </div>
                    <div>
                      <strong>Validation</strong>
                      <ul className="mt-1 mb-0 list-disc pl-5">
                        <li>Required fields must be present.</li>
                        <li>Employee IDs cannot be duplicated.</li>
                        <li>Emails must be valid when provided.</li>
                        <li>Manager IDs must match imported managers.</li>
                      </ul>
                    </div>
                    <div>
                      <strong>Managers</strong>
                      <ul className="mt-1 mb-0 list-disc pl-5">
                        <li>Use for everyone who needs login access.</li>
                        <li>Email is required.</li>
                        <li>Only top-level leaders may have blank manager_employee_id.</li>
                      </ul>
                    </div>
                    <div>
                      <strong>Employees</strong>
                      <ul className="mt-1 mb-0 list-disc pl-5">
                        <li>Use for staff who do not need login access.</li>
                        <li>Email is optional.</li>
                        <li>
                          manager_employee_id must match an imported manager.
                        </li>
                        <li>
                          Leave it blank to use the manager of the row's
                          department. That department needs exactly one manager,
                          otherwise the row is rejected.
                        </li>
                      </ul>
                    </div>
                    <div>
                      <strong>Employee IDs</strong>
                      <ul className="mt-1 mb-0 list-disc pl-5">
                        <li>
                          employee_id is optional. A blank one is taken from the
                          person's existing record, matched on email, or an
                          AUTO- identifier is assigned.
                        </li>
                        <li>
                          Supply the real employee_id whenever you have it. A
                          row with no employee_id and no email is imported as a
                          new person every time.
                        </li>
                      </ul>
                    </div>
                    <div>
                      <strong>Departments</strong>
                      <ul className="mt-1 mb-0 list-disc pl-5">
                        <li>dept_name is the department/unit name.</li>
                        <li>
                          dept_id is optional. Leave the column out or leave it
                          blank and the code of the matching department is used,
                          or a new one is assigned for a new department name.
                        </li>
                        <li>The same dept_id should always use the same dept_name.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              }
              type="info"
              showIcon
            />
            <Row justify="space-between">
              <Col md={11} xs={20}>
                <Form.Item label="Hospital">
                  <Form.Item
                    name="hospital_id"
                    rules={[
                      { required: true, message: "Please choose a Hospital" },
                    ]}
                  >
                    <Select
                      placeholder="Select A Hospital"
                      loading={isLoadingHospitals}
                      onChange={(value) => {
                        setHospitalId(value);
                      }}
                      options={hospitals}
                    ></Select>
                  </Form.Item>
                </Form.Item>
              </Col>
              <Col md={11} xs={20}>
                <Form.Item label="Upload Type">
                  <Form.Item
                    name="import_type"
                    rules={[
                      { required: true, message: "Choose an Import Type" },
                    ]}
                  >
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Select Import Type"
                      onChange={(value) => {
                        setImportType(value);
                      }}
                      options={[
                        {
                          label: "Managers",
                          value: "managers",
                        },
                        {
                          label: "Employees",
                          value: "employees",
                        },
                      ]}
                    />
                  </Form.Item>
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col md={10} xs={24}>
                <Form.Item label="Input File">
                  <Form.Item
                    name="inputFile"
                    rules={[{ required: true, message: "Attach A File" }]}
                  >
                    <Upload
                      multiple={false}
                      maxCount={1}
                      {...props}
                    >
                      <Button
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold px-4 rounded"
                        icon={<UploadOutlined />}
                      >
                        Upload csv only
                      </Button>
                    </Upload>
                  </Form.Item>
                </Form.Item>
              </Col>
            </Row>
            <Row justify="space-between">
              <Col md={10} xs={24}>
                <Form.Item>
                  <Form.Item>
                    <Button
                      onClick={handleUpload}
                      disabled={fileList.length === 0 || !hospitalId || !importType || uploading}
                      loading={uploading}
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold px-4 rounded"
                      htmlType="submit"
                    >
                      Submit
                    </Button>
                  </Form.Item>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </>
  );
}
